import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreate } from '@services/staff/benchUsers/create';
import { useTranslation } from 'react-i18next';
import { BenchUser } from '@root/globalTypes';

export function useCreateBenchUserForm({
  successHandler,
}: {
  successHandler: (data: BenchUser) => void;
}) {
  const { t } = useTranslation('app');

  const schema = z.object({
    username: z
      .string()
      .trim()
      .min(1, { message: t('form.username.required') }),
    password: z
      .string()
      .trim()
      .min(8, { message: t('form.password.complexity') }),
    expiresAt: z.date(),
  });

  const defaultValues: z.infer<typeof schema> = {
    username: '',
    password: '',
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
  };

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { mutate, error, isPending } = useCreate({
    callback: successHandler,
  });

  const onSubmit = async (values: z.infer<typeof schema>) => {
    mutate({ ...values, expiresAt: values.expiresAt.toISOString() });
  };

  return {
    form,
    onSubmit,
    isPending,
    error,
  };
}
